/**
 * HealthTab - Pestaña de estado y mediciones dendrométricas
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-tools/TreeDetailsPanel
 */

import type { Tree } from '../../tree-types';
import { FormLabel } from '../FormLabel';

export interface HealthTabProps {
  tree: Tree;
  onChange: (field: keyof Tree, value: any) => void;
}

export function HealthTab({ tree, onChange }: HealthTabProps): JSX.Element {
  return (
    <div className="tree-tab-panel">
      {/* Sección: Mediciones Dendrométricas */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Mediciones Dendrométricas</h3>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Diámetro a la Altura del Pecho: se mide el diámetro del tronco a 1.30 metros desde el suelo. Es la medida estándar para evaluar el tamaño del árbol.">
              DAP (cm)
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.dbh || ''}
              onChange={(e) => onChange('dbh', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.0"
              step="0.1"
              min="0"
            />
            <div className="tree-form-hint">Diámetro a 1.30m del suelo</div>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Altura total del árbol desde el suelo hasta el punto más alto de la copa, medida en metros.">
              Altura total (m)
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.totalHeight || ''}
              onChange={(e) => onChange('totalHeight', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.0"
              step="0.1"
              min="0"
            />
          </div>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Altura del tronco desde el suelo hasta donde comienzan las primeras ramas principales. Importante para evaluar espacios libres bajo la copa.">
              Altura 1ª ramificación (m)
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.firstBranchHeight || ''}
              onChange={(e) => onChange('firstBranchHeight', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.0"
              step="0.1"
              min="0"
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Diámetro promedio de la copa del árbol, medido desde los extremos más alejados del follaje. Útil para evaluar sombra y espacio ocupado.">
              Diámetro de copa (m)
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.crownDiameter || ''}
              onChange={(e) => onChange('crownDiameter', e.target.value ? parseFloat(e.target.value) : undefined)}
              placeholder="0.0"
              step="0.1"
              min="0"
            />
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Forma o silueta general de la copa del árbol. Varía según la especie y las condiciones de crecimiento (podas, espacio disponible, etc.).">
            Forma de copa
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.crownShape}
            onChange={(e) => onChange('crownShape', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="columnar">Columnar</option>
            <option value="globose">Globosa</option>
            <option value="pyramidal">Piramidal</option>
            <option value="irregular">Irregular</option>
            <option value="umbrella">Aparasolada</option>
          </select>
        </div>
      </div>

      {/* Sección: Estado Fitosanitario */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Estado Fitosanitario</h3>

        <div className="tree-form-group">
          <FormLabel
            required
            tooltip="Evaluación general del estado de salud y vigor del árbol. Considera el conjunto de follaje, tronco, ramas y raíces."
          >
            Estado general
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.generalCondition}
            onChange={(e) => onChange('generalCondition', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="excellent">Excelente</option>
            <option value="good">Bueno</option>
            <option value="fair">Regular</option>
            <option value="poor">Malo</option>
            <option value="critical">Crítico</option>
            <option value="dead">Seco</option>
          </select>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Condición del sistema radicular. Raíces expuestas pueden indicar erosión del suelo; raíces levantando pavimento requieren intervención.">
              Estado de raíces
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.rootsCondition}
              onChange={(e) => onChange('rootsCondition', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="healthy">Sanas</option>
              <option value="exposed">Expuestas</option>
              <option value="damaged">Dañadas</option>
              <option value="lifting-pavement">Levantando pavimento</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Condición del tronco principal. Cavidades, grietas o podredumbre pueden comprometer la estabilidad estructural del árbol.">
              Estado del tronco
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.trunkCondition}
              onChange={(e) => onChange('trunkCondition', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="healthy">Sano</option>
              <option value="cavities">Cavidades</option>
              <option value="cracks">Grietas</option>
              <option value="rot">Podredumbre</option>
              <option value="inclined">Inclinado</option>
            </select>
          </div>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Condición de las ramas principales y secundarias. Ramas quebradas o secas pueden representar riesgo de caída.">
              Estado de ramas
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.branchesCondition}
              onChange={(e) => onChange('branchesCondition', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="healthy">Sanas</option>
              <option value="broken">Quebradas</option>
              <option value="dry">Secas</option>
              <option value="dangerous">Peligrosas</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Condición de las hojas. Follaje débil, amarillento o con manchas puede indicar problemas de nutrición, plagas o enfermedades.">
              Estado del follaje
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.foliageCondition}
              onChange={(e) => onChange('foliageCondition', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="vigorous">Vigoroso</option>
              <option value="normal">Normal</option>
              <option value="weak">Débil</option>
              <option value="yellowish">Amarillento</option>
              <option value="spotted">Con manchas</option>
            </select>
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Ángulo de inclinación del tronco respecto a la vertical. Inclinaciones mayores a 15° pueden representar riesgo de caída y requieren evaluación.">
            Inclinación (grados)
          </FormLabel>
          <input
            type="number"
            className="tree-form-input"
            value={tree.inclination || ''}
            onChange={(e) => onChange('inclination', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="0"
            min="0"
            max="90"
          />
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="hasCavities"
            className="tree-form-checkbox"
            checked={tree.hasCavities}
            onChange={(e) => onChange('hasCavities', e.target.checked)}
          />
          <label htmlFor="hasCavities" className="tree-form-checkbox-label">
            Presenta cavidades/huecos
          </label>
        </div>

        {tree.hasCavities && (
          <div className="tree-form-group">
            <FormLabel tooltip="Detallar la ubicación (altura, orientación), tamaño aproximado y características de las cavidades detectadas en el árbol.">
              Descripción de cavidades
            </FormLabel>
            <textarea
              className="tree-form-textarea"
              value={tree.cavitiesDescription}
              onChange={(e) => onChange('cavitiesDescription', e.target.value)}
              placeholder="Describir ubicación, tamaño y características..."
            />
          </div>
        )}

        <div className="tree-form-group">
          <FormLabel tooltip="Organismos que se alimentan del árbol causando daño. Ejemplos: pulgones (chupan savia), cochinillas, orugas defoliadoras, barrenadores, hormigas cortadoras, etc.">
            Plagas detectadas (separar con coma)
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={(tree.pests ?? []).join(', ')}
            onChange={(e) => onChange('pests', e.target.value.split(',').map(p => p.trim()).filter(p => p))}
            placeholder="Ej: pulgones, cochinillas, orugas"
          />
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Afecciones causadas por patógenos. Hongos (manchas, cancros, podredumbres), virus (mosaicos, deformaciones) o bacterias (agallas, marchitez). Requieren identificación profesional.">
            Enfermedades (separar con coma)
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={(tree.diseases ?? []).join(', ')}
            onChange={(e) => onChange('diseases', e.target.value.split(',').map(d => d.trim()).filter(d => d))}
            placeholder="Ej: hongos, virus, bacterias"
          />
        </div>
      </div>
    </div>
  );
}
